import angular from 'angular';
import paymentDistributionDialogTemplate from './PaymentDistributionDialog.html';
import CSMCommonWeb from 'csm-common-web';

export default angular
  .module('csm.books.com.PaymentDistributionDialog', [
    CSMCommonWeb,
    'ui.bootstrap'
  ])
  .service('PaymentDistributionDialog', PaymentDistributionDialogService).name;

/* @ngInject */ function PaymentDistributionDialogService(
  $uibModal,
  uiGridConstants,
  Tools
) {
  /*****************************************************************************
   * show */
  /**
   * This will display a modal dialog to manage the distribution of the cash
   * balance across the provided receiptItems.
   *
   * @param  receiptItems  List of ReceiptItems to pay against
   * @param  entity = { name: string; id: number } - the receipt's own From
   *         entity. Used as the charge's entityID/name fallback whenever a
   *         receiptItem has no distinct entity of its own (e.g. a
   *         non-family-member sale) - the item's own entity is always
   *         preferred when it exists.
   * @param  distributionTotal  Payment distributionTotal to distribute
   * @return        Promise to listen on
   *****************************************************************************/
  this.show = function (receiptItems, entity, distributionTotal) {
    return $uibModal.open({
      animation: true,
      backdrop: 'static',
      size: 'md',
      template: paymentDistributionDialogTemplate,
      resolve: {
        receiptItems: function () {
          return receiptItems;
        },
        entity: function () {
          return entity;
        },
        distributionTotal: function () {
          return distributionTotal;
        }
      },
      controller: PaymentDistributionController
    }).result;
  };

  /*****************************************************************************
   * Dialog Controller
   *****************************************************************************/
  /* @ngInject */ function PaymentDistributionController(
    $scope,
    CommonUIGridTemplates,
    Alert,
    SERVER,
    receiptItems,
    entity,
    distributionTotal
  ) {
    /***************************************************************************
     * Class: DGItem
     *
     * Distribution datagrid list item
     ***************************************************************************/
    function DGItem(receiptItem) {
      /** Receipt Item Source. */
      this.receiptItem = receiptItem;

      /** Distribution Amount. */
      var mAmount;

      /*------------------------------------*/
      /* Initialize amount to be the item   */
      /* amount less the on account balance */
      /*------------------------------------*/
      mAmount = receiptItem.receivable
        ? Tools.roundMoney(receiptItem.amount - receiptItem.receivable.amount)
        : 0;

      /** Source Property accessors. */
      Object.defineProperty(this, 'memo', {
        get: function () {
          return receiptItem.memo;
        }
      });
      Object.defineProperty(this, 'total', {
        get: function () {
          return receiptItem.amount;
        }
      });

      /** Amount Accessor. On a change, update the remaining balance. */
      Object.defineProperty(this, 'amount', {
        get: function () {
          return mAmount;
        },
        set: function (value) {
          /** Ensure amount does not exceed item amount and does not go below zero. */
          var num = Math.max(
            Math.min(angular.isNumber(value) ? value : 0, receiptItem.amount),
            0
          );

          var changed = mAmount != num;
          mAmount = num;

          if (changed) updateRemaining();
        }
      });
    }

    /** Payment Total to distribute. */
    $scope.distributionTotal = distributionTotal;

    /*--------------*/
    /* Grid Options */
    /*--------------*/
    $scope.options = {
      enableColumnMenus: false,
      enableCellEdit: false,
      enableSorting: false,
      enableHorizontalScrollbar: uiGridConstants.scrollbars.NEVER,
      enableVerticalScrollbar: uiGridConstants.scrollbars.WHEN_NEEDED,
      columnDefs: [
        {
          field: 'memo',
          displayName: 'Description'
        },
        {
          field: 'total',
          displayName: 'Total Price',
          maxWidth: 120,
          cellFilter: 'currency'
        },
        {
          field: 'amount',
          maxWidth: 120,
          cellFilter: 'currency',
          enableCellEdit: true,
          enableCellEditOnFocus: true,
          editableCellTemplate: CommonUIGridTemplates.CELL_NUMBER_INPUT,
          precision: 2
        }
      ],
      data: receiptItems.map(function (i) {
        return new DGItem(i);
      })
    };

    /** Clear any notification and dismiss the modal. */
    $scope.back = function () {
      Alert.clear();
      $scope.$dismiss();
    };

    /** Done button handler. Ensure distributionTotal has been distributed and update
      receivable property on receiptItems before resolving modal promise. */
    $scope.done = function () {
      Alert.clear();

      if ($scope.remainder != 0) {
        Alert.info(
          'The full payment amount needs to be distributed',
          'Cannot Continue'
        );
        return;
      }

      /*-----------------------------------------*/
      /* Update receivable property on the receiptItems */
      /*-----------------------------------------*/
      $scope.options.data.forEach(function (dgItem) {
        var debitAmount = Tools.roundMoney(dgItem.total - dgItem.amount);
        var needCharge = debitAmount > 0;
        var receiptItem = dgItem.receiptItem;
        var charge = receiptItem.receivable || {
          type: SERVER.BooksDBConstants.LIT_RECEIVABLE_DEBIT,
          subType: SERVER.BooksDBConstants.LIS_RECEIVABLE_ITEM_CHARGE
        };

        /*------------------------*/
        /* Update the charge item */
        /*------------------------*/
        if (needCharge) {
          charge.referenceItemID = receiptItem.id;
          charge.amount = debitAmount;
          charge.entityID = receiptItem.entityID || entity.id;
          charge.name = receiptItem.name || entity.name;
        }

        /*--------------------------------*/
        /* Update the receivable property */
        /*--------------------------------*/
        receiptItem.receivable = needCharge ? charge : null;
      });

      /*--------------------------------------------------*/
      /* Everything is updated, resolve the modal promise */
      /*--------------------------------------------------*/
      $scope.$close();
    };

    /** Updates the remaining amount property from the distributed amounts. */
    function updateRemaining() {
      var amt = 0;
      $scope.options.data.forEach(function (dgItem) {
        amt += dgItem.amount;
      });

      $scope.remainder = Tools.roundMoney($scope.distributionTotal - amt);
    }

    /*------------------------------------*/
    /* Initialize the distributed amounts */
    /* until the payment total is met     */
    /*------------------------------------*/
    var paidItems = [],
      nonPaidItems = [],
      balance = $scope.distributionTotal;
    $scope.options.data.forEach(function (dgItem) {
      (dgItem.amount ? paidItems : nonPaidItems).push(dgItem);
      balance -= dgItem.amount;
    });

    /*------------------------------------------------------------*/
    /* Distribute the remaining balance across the non-Paid receiptItems */
    /*------------------------------------------------------------*/
    nonPaidItems.forEach(function (dgItem) {
      if (balance <= 0) return;

      dgItem.amount = Math.min(dgItem.total, balance);

      balance -= dgItem.amount;
    });

    /*---------------------------------*/
    /* Initialize the remaining amount */
    /*---------------------------------*/
    updateRemaining();
  }
}
